import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Check, User, Save, Upload, Sparkles, CheckCircle2, ArrowRight, Loader2, Trash2, Users, Copy, Star, BookOpen } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { useMentorSignupEnabled } from '../hooks/useMentorSignupEnabled';
import * as api from '../lib/api';
import { defaultAvatar } from '../lib/avatarDefaults';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

/* ── Mentor Add-On Payment Form ── */
const MentorAddonForm = ({ userId, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError(null);
    try {
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });
      if (pmError) { setError(pmError.message); setLoading(false); return; }

      const res = await api.createMentorSubscription({
        user_id: userId,
        payment_method_id: paymentMethod.id,
      });
      const { client_secret, status } = res.data;

      if (status === 'active') { onSuccess(); return; }

      if (client_secret) {
        const { error: confirmError } = await stripe.confirmCardPayment(client_secret);
        if (confirmError) setError(confirmError.message);
        else onSuccess();
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Payment failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="mentor-addon-payment-form">
      <div className="p-4 bg-white rounded-arch border border-primary/20">
        <CardElement options={{ style: { base: { fontSize: '15px', color: '#2B2814', '::placeholder': { color: '#999' } }, invalid: { color: '#ef4444' } } }} />
      </div>
      {error && <p className="text-sm text-red-500" role="alert">{error}</p>}
      <button
        type="submit"
        disabled={!stripe || loading}
        className="w-full py-3 rounded-arch font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
        style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
        data-testid="mentor-addon-pay-btn"
      >
        {loading
          ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Processing...</>
          : <><Star className="w-4 h-4" aria-hidden="true" /> Subscribe — $5/month</>}
      </button>
      <p className="text-[11px] text-text-muted text-center">Cancel anytime from your profile.</p>
    </form>
  );
};


const ProfilePage = ({ onBack, onNavigate }) => {
  const { profile, updateProfile, user, signOut } = useAuth();
  const mentorSignupEnabled = useMentorSignupEnabled();
  const maxNameLen = (profile?.is_admin || profile?.is_mentor) ? 18 : 15;
  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [bio, setBio] = useState(profile?.bio || '');
  const [selectedAvatar, setSelectedAvatar] = useState(profile?.avatar_url || '');
  const [showBrothers, setShowBrothers] = useState(profile?.show_brothers !== false);
  const [showSisters, setShowSisters] = useState(profile?.show_sisters !== false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [avatarOptions, setAvatarOptions] = useState([]);
  const [avatarsLoading, setAvatarsLoading] = useState(true);
  const [customUploading, setCustomUploading] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [nameStatus, setNameStatus] = useState(null);
  const [nameError, setNameError] = useState('');
  const [profileVisibility, setProfileVisibility] = useState(profile?.profile_visibility || 'everyone');
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [subStatus, setSubStatus] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [inviteStats, setInviteStats] = useState(null);
  const [copied, setCopied] = useState(false);
  // Mentor add-on
  const [showMentorAddon, setShowMentorAddon] = useState(false);
  const [mentorAddonSuccess, setMentorAddonSuccess] = useState(false);
  const [cancelMentorLoading, setCancelMentorLoading] = useState(false);
  // Mentor profile fields
  const [mentorBio, setMentorBio] = useState(profile?.mentor_bio || '');
  const [mentorSpecialty, setMentorSpecialty] = useState(profile?.mentor_specialty || '');
  const [mentorSaveStatus, setMentorSaveStatus] = useState(null); // 'success' | 'error' | null
  const [mentorSaveError, setMentorSaveError] = useState('');

  // Only seed form fields from `profile` once per profile load — re-running this
  // on every profile refresh (e.g. after a background token refresh) would
  // overwrite in-progress, unsaved edits with stale server values.
  const seededProfileId = React.useRef(null);
  useEffect(() => {
    if (profile && seededProfileId.current !== profile.id) {
      seededProfileId.current = profile.id;
      setDisplayName(profile.display_name || '');
      setBio(profile.bio || '');
      setSelectedAvatar(profile.avatar_url || '');
      setShowBrothers(profile.show_brothers !== false);
      setShowSisters(profile.show_sisters !== false);
      setOnboardingStatus(profile.onboarding_completed || false);
      setMentorBio(profile.mentor_bio || '');
      setMentorSpecialty(profile.mentor_specialty || '');
    }
  }, [profile]);

  // Fetch gender-specific avatars from the database
  useEffect(() => {
    const fetchAvatars = async () => {
      setAvatarsLoading(true);
      try {
        const gender = profile?.gender || 'brother';
        const res = await api.getAvatars(gender, profile?.id);
        setAvatarOptions(res.data || []);
      } catch (err) {
        setAvatarOptions([]);
      } finally {
        setAvatarsLoading(false);
      }
    };
    if (profile) fetchAvatars();
  }, [profile?.gender, profile]);

  // Load blocked users
  useEffect(() => {
    if (!profile?.id) return;
    api.getBlockedUsers(profile.id).then(r => setBlockedUsers(r.data || [])).catch(() => {});
  }, [profile?.id]);

  // Load subscription status
  useEffect(() => {
    if (!profile?.id) return;
    api.getSubscriptionStatus(profile.id).then(r => setSubStatus(r.data)).catch(() => {});
  }, [profile?.id]);

  // Load invite stats
  useEffect(() => {
    if (!profile?.id) return;
    api.getInviteStats(profile.id).then(r => setInviteStats(r.data)).catch(() => {});
  }, [profile?.id]);

  const handleCopyInviteLink = () => {
    const link = `https://sirati.co/invite/${profile?.display_name}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleCancelSubscription = async () => {
    if (!profile?.id) return;
    if (!window.confirm('Cancel your Sirati+ subscription? It will remain active until the end of your current billing period.')) return;
    setCancelLoading(true);
    try {
      await api.cancelStripeSubscription(profile.id);
      const r = await api.getSubscriptionStatus(profile.id);
      setSubStatus(r.data);
      setCancelSuccess(true);
      setTimeout(() => setCancelSuccess(false), 4000);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };



  const handleCancelMentorSubscription = async () => {
    if (!profile?.id) return;
    if (!window.confirm('Cancel your Mentor Messaging add-on? You will lose access at the end of the billing period.')) return;
    setCancelMentorLoading(true);
    try {
      await api.cancelMentorSubscription(profile.id);
      const r = await api.getSubscriptionStatus(profile.id);
      setSubStatus(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to cancel mentor subscription');
    } finally {
      setCancelMentorLoading(false);
    }
  };

  const handleSaveMentorProfile = async () => {
    try {
      await api.updateMentorProfile({ mentor_bio: mentorBio, mentor_specialty: mentorSpecialty });
      setMentorSaveStatus('success');
      setTimeout(() => setMentorSaveStatus(null), 3000);
    } catch (err) {
      setMentorSaveError(err?.response?.data?.detail || 'Failed to save mentor profile');
      setMentorSaveStatus('error');
    }
  };

  // Real-time display name availability check
  useEffect(() => {
    const name = displayName.trim();
    const nameLower = name.toLowerCase();
    if (!name) { setNameStatus(null); setNameError(''); return; }
    if (name.length > maxNameLen) { setNameStatus('invalid'); setNameError(`Max ${maxNameLen} characters`); return; }
    if (!/^[a-zA-Z.]+$/.test(name)) { setNameStatus('invalid'); setNameError('Username can only contain letters and periods'); return; }
    if (['sakeena','sirati','siratii'].includes(nameLower)) {
      // Allow current user to keep their own reserved name
      if (nameLower !== (profile?.display_name || '').toLowerCase()) {
        setNameStatus('taken'); setNameError('This username is reserved'); return;
      }
    }
    if (nameLower.includes('admin') || nameLower.includes('moderator')) {
      setNameStatus('invalid'); setNameError("Username cannot contain 'admin' or 'moderator'"); return;
    }
    if (nameLower === (profile?.display_name || '').toLowerCase()) { setNameStatus('available'); setNameError(''); return; }
    setNameStatus('checking');
    const timer = setTimeout(async () => {
      try {
        const res = await api.checkDisplayName(name, profile?.id);
        if (res.data?.available) { setNameStatus('available'); setNameError(''); }
        else { setNameStatus('taken'); setNameError(res.data?.reason || 'Name taken'); }
      } catch { setNameStatus(null); }
    }, 400);
    return () => clearTimeout(timer);
  }, [displayName, profile?.display_name, profile?.id, maxNameLen]);



  const handleCustomAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      setError('Please select a JPEG, PNG, WebP, GIF, or SVG image.');
      return;
    }
    setCustomUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', `custom-${profile.display_name || 'admin'}`);
      formData.append('gender', profile.gender || 'brother');
      const res = await api.uploadAvatar(formData);
      setSelectedAvatar(res.data.url);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to upload custom avatar');
    } finally {
      setCustomUploading(false);
    }
  };

  const handleSave = async () => {
    const name = displayName.trim();
    if (!name) { setError('Display name is required'); return; }
    if (nameStatus === 'taken' || nameStatus === 'invalid') { setError(nameError || 'Fix display name first'); return; }
    setLoading(true);
    setError('');
    setSuccess(false);
    try {
      await updateProfile({
        display_name: name,
        bio: bio.trim(),
        avatar_url: selectedAvatar,
        show_brothers: showBrothers,
        show_sisters: showSisters,
        profile_visibility: profileVisibility,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user?.id) return;
    setDeleteLoading(true);
    try {
      await api.deleteAccount(user.id);
      await signOut();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to delete account. Please try again.');
      setShowDeleteConfirm(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-arch-xl overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-white/20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-primary/10 rounded-full transition-colors" data-testid="profile-back-btn">
              <ChevronLeft className="w-6 h-6 text-primary" />
            </button>
            <h1 className="font-heading text-2xl font-semibold text-primary">Edit Profile</h1>
          </div>
          <div className="flex items-center gap-3">
            {profile?.is_mentor && (
              <button
                onClick={() => onNavigate?.('mentor-hub')}
                className="text-sm font-medium flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors"
                style={{ background: 'rgba(234,179,8,0.12)', color: '#92680a' }}
                data-testid="mentor-hub-link"
              >
                <Star className="w-3.5 h-3.5" aria-hidden="true" />
                Mentor Hub
              </button>
            )}
            {profile?.display_name && (
              <button
                onClick={() => onNavigate?.(`user:${profile.display_name}`)}
                className="text-sm text-primary hover:underline font-medium"
                data-testid="my-profile-link"
              >
                My Profile →
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {error && (
            <div className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-3 rounded-arch">{error}</div>
          )}
          {success && (
            <div className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-4 py-3 rounded-arch flex items-center gap-2">
              <Check className="w-5 h-5" /> Profile updated successfully!
            </div>
          )}

          {/* Onboarding Section */}
          <div className="p-5 rounded-arch-lg border border-primary/15"
            style={{ background: 'linear-gradient(135deg, rgba(74,173,168,0.05), rgba(169,156,200,0.05))' }}
            data-testid="profile-onboarding-section"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: onboardingStatus ? 'rgba(34,197,94,0.1)' : 'rgba(74,173,168,0.1)' }}>
                  {onboardingStatus ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : (
                    <Sparkles className="w-5 h-5 text-primary" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-sm text-text-main">
                    {onboardingStatus ? 'Readiness Journey Complete' : 'Start Your Readiness Journey'}
                  </p>
                  <p className="text-xs text-text-muted mt-0.5">
                    {onboardingStatus
                      ? 'Your goals are personalized on your dashboard. You can update your answers anytime.'
                      : 'Answer 6 quick questions to personalize your dashboard goals and financial tracker.'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => onNavigate?.('onboarding')}
                className={`px-4 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 flex-shrink-0 transition-colors ${
                  onboardingStatus
                    ? 'bg-primary/10 text-primary hover:bg-primary/20'
                    : 'bg-primary text-white btn-hover'
                }`}
                data-testid="profile-onboarding-btn"
              >
                {onboardingStatus ? 'Update Answers' : 'Begin'} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Sirati+ — Subscription Management */}
          <div className="p-5 rounded-arch-lg border border-primary/15"
            style={{ background: 'linear-gradient(135deg, rgba(74,173,168,0.08), rgba(169,156,200,0.08))' }}
            data-testid="premium-section">
            {cancelSuccess && (
              <div className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-3 py-2 rounded-arch mb-3 text-xs flex items-center gap-2">
                <Check className="w-4 h-4" /> Cancellation scheduled. Sirati+ stays active until period end.
              </div>
            )}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: subStatus?.is_premium ? 'rgba(74,173,168,0.15)' : 'rgba(169,156,200,0.15)' }}>
                  <Sparkles className={`w-5 h-5 ${subStatus?.is_premium ? 'text-primary' : 'text-text-muted'}`} />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-sm text-text-main flex items-center gap-2">
                    {subStatus?.is_premium ? 'Sirati+' : 'Upgrade to Sirati+'}
                    {subStatus?.is_beta_member && (
                      <span className="px-2 py-0.5 bg-primary/10 text-primary text-[10px] rounded-full font-medium">Founding Member</span>
                    )}
                  </p>
                  <p className="text-xs text-text-muted mt-0.5">
                    {subStatus?.is_premium ? (
                      <>
                        {{ yearly: 'Yearly', lifetime: 'Lifetime', monthly: 'Monthly' }[subStatus.premium_tier] || 'Monthly'} plan
                        {subStatus.cancel_at_period_end && subStatus.current_period_end && (
                          <> — ending {new Date(subStatus.current_period_end * 1000).toLocaleDateString()}</>
                        )}
                      </>
                    ) : (
                      'Connect more, personalize your profile, and get more access to live sessions, events and partner perks.'
                    )}
                  </p>
                </div>
              </div>
              {subStatus?.is_premium ? (
                subStatus.premium_tier === 'lifetime' ? (
                  <span className="px-3 py-1 text-xs text-text-muted">Lifetime member</span>
                ) : !subStatus.cancel_at_period_end ? (
                  <button onClick={handleCancelSubscription} disabled={cancelLoading}
                    className="px-4 py-2 max-sm:px-2 max-sm:py-1 rounded-full text-xs max-sm:text-[10px] font-medium bg-red-50 text-red-600 hover:bg-red-100 border border-red-100 flex items-center gap-1.5 max-sm:gap-1 flex-shrink-0 disabled:opacity-50"
                    data-testid="cancel-subscription-btn">
                    {cancelLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    Cancel Subscription
                  </button>
                ) : (
                  <span className="px-3 py-1 text-xs text-text-muted">Cancellation pending</span>
                )
              ) : (
                <button onClick={() => onNavigate?.('premium')}
                  className="px-4 py-2 rounded-full text-xs font-medium bg-primary text-white hover:bg-primary/90 flex items-center gap-1.5 flex-shrink-0"
                  data-testid="upgrade-premium-edit-btn">
                  <Sparkles className="w-3.5 h-3.5" /> Upgrade
                </button>
              )}
            </div>
          </div>

          {/* Mentor Messaging Add-On — not shown to mentors/admins (they don't need to pay to message mentors) */}
          {subStatus?.is_premium && !profile?.is_mentor && !profile?.is_admin && (
            <div
              className="p-5 rounded-arch-lg border"
              style={{ background: 'rgba(234,179,8,0.05)', borderColor: 'rgba(234,179,8,0.2)' }}
              data-testid="mentor-addon-section"
            >
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(234,179,8,0.12)' }}>
                    <Star className="w-5 h-5 text-yellow-700" aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-text-main flex items-center gap-2">
                      Mentor Messaging
                      {profile?.is_mentor_subscriber && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: 'rgba(74,173,168,0.12)', color: '#2a7b77' }}>Active</span>
                      )}
                    </p>
                    <p className="text-xs text-text-muted mt-0.5">
                      {profile?.is_mentor_subscriber
                        ? 'You can send messages directly to Sirati mentors.'
                        : mentorSignupEnabled === false
                          ? 'Coming soon. We are welcoming our first mentors, and you will be able to message them here.'
                          : 'Add on access to message Sirati mentors — $5/month on top of your Sirati+ plan.'}
                    </p>
                  </div>
                </div>
                {profile?.is_mentor_subscriber ? (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => onNavigate?.('mentors')}
                      className="px-4 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors"
                      style={{ background: 'rgba(74,173,168,0.12)', color: '#2a7b77', border: '1px solid rgba(74,173,168,0.25)' }}
                      data-testid="browse-mentors-btn"
                    >
                      Browse Mentors →
                    </button>
                    <button
                      onClick={handleCancelMentorSubscription}
                      disabled={cancelMentorLoading}
                      className="px-4 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
                      style={{ background: 'rgba(239,68,68,0.08)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.2)' }}
                      data-testid="cancel-mentor-addon-btn"
                    >
                      {cancelMentorLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : null}
                      Cancel Add-On
                    </button>
                  </div>
                ) : mentorSignupEnabled === false ? (
                  <span className="px-4 py-2 rounded-full text-xs font-medium flex-shrink-0" style={{ background: 'rgba(169,156,200,0.18)', color: '#7d6dab' }} data-testid="mentor-addon-coming-soon">
                    Coming soon
                  </span>
                ) : mentorSignupEnabled === null ? null : (
                  <button
                    onClick={() => setShowMentorAddon(!showMentorAddon)}
                    className="px-4 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 flex-shrink-0 transition-colors"
                    style={{ background: 'rgba(234,179,8,0.15)', color: '#92680a', border: '1px solid rgba(234,179,8,0.25)' }}
                    data-testid="add-mentor-addon-btn"
                  >
                    <Star className="w-3.5 h-3.5" aria-hidden="true" />
                    {showMentorAddon ? 'Hide' : 'Add On'}
                  </button>
                )}
              </div>

              {mentorAddonSuccess && (
                <div className="mt-4 flex items-center gap-2 p-3 rounded-arch text-sm font-medium" style={{ background: 'rgba(74,173,168,0.1)', color: '#2a7b77' }} role="status" aria-live="polite">
                  <Check className="w-4 h-4" aria-hidden="true" /> Mentor Messaging activated! Visit the Mentors page to get started.
                </div>
              )}

              {showMentorAddon && mentorSignupEnabled && !profile?.is_mentor_subscriber && (
                <Elements stripe={stripePromise}>
                  <MentorAddonForm
                    userId={profile?.id}
                    onSuccess={() => {
                      setMentorAddonSuccess(true);
                      setShowMentorAddon(false);
                      setTimeout(() => setMentorAddonSuccess(false), 6000);
                    }}
                  />
                </Elements>
              )}
            </div>
          )}

          {/* Mentor Profile (visible to mentors only) */}
          {profile?.is_mentor && (
            <div
              className="p-5 rounded-arch-lg border"
              style={{ background: 'rgba(74,173,168,0.05)', borderColor: 'rgba(74,173,168,0.2)' }}
              data-testid="mentor-profile-section"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(74,173,168,0.12)' }}>
                  <Star className="w-5 h-5 text-accent-teal" aria-hidden="true" />
                </div>
                <div>
                  <p className="font-medium text-sm text-text-main">Mentor Profile</p>
                  <p className="text-xs text-text-muted">Visible to Sirati+ members browsing mentors</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label htmlFor="mentor-specialty-input" className="block text-sm font-medium text-text-main mb-1.5">
                    Specialty <span className="text-text-muted font-normal">(e.g. Financial Readiness, Emotional Growth)</span>
                  </label>
                  <div className="relative">
                    <BookOpen className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" aria-hidden="true" />
                    <input
                      id="mentor-specialty-input"
                      type="text"
                      value={mentorSpecialty}
                      onChange={(e) => setMentorSpecialty(e.target.value.slice(0, 100))}
                      maxLength={100}
                      placeholder="Your area of expertise..."
                      className="w-full pl-11 pr-4 py-3 rounded-arch text-sm focus:ring-2 focus:ring-primary transition-all"
                      style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid var(--border)', color: 'var(--text-main)' }}
                      data-testid="mentor-specialty-input"
                    />
                  </div>
                  <p className="text-[10px] text-text-muted mt-1">{mentorSpecialty.length}/100</p>
                </div>
                <div>
                  <label htmlFor="mentor-bio-input" className="block text-sm font-medium text-text-main mb-1.5">
                    Mentor Bio <span className="text-text-muted font-normal">(optional, max 300 characters)</span>
                  </label>
                  <div className="relative">
                    <textarea
                      id="mentor-bio-input"
                      value={mentorBio}
                      onChange={(e) => setMentorBio(e.target.value.slice(0, 300))}
                      rows={4}
                      placeholder="Tell mentees about your background and how you can help..."
                      className="w-full px-4 py-3 rounded-arch text-sm resize-none focus:ring-2 focus:ring-primary transition-all"
                      style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid var(--border)', color: 'var(--text-main)' }}
                      data-testid="mentor-bio-input"
                    />
                    <span
                      className="absolute bottom-3 right-4 text-[10px] pointer-events-none"
                      style={{ color: mentorBio.length >= 300 ? '#dc2626' : 'var(--text-muted)' }}
                    >
                      {mentorBio.length}/300
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleSaveMentorProfile}
                  className="px-5 py-2 rounded-arch text-sm font-medium transition-colors flex items-center gap-2"
                  style={{ background: 'rgba(74,173,168,0.12)', color: '#2a7b77', border: '1px solid rgba(74,173,168,0.25)' }}
                  data-testid="save-mentor-profile-btn"
                >
                  <Save className="w-4 h-4" aria-hidden="true" />
                  Save Mentor Profile
                </button>
                <p aria-live="polite" className="text-sm">
                  {mentorSaveStatus === 'success' && (
                    <span className="text-green-700 dark:text-green-400 flex items-center gap-1.5" data-testid="mentor-profile-save-success">
                      <Check className="w-4 h-4" aria-hidden="true" /> Mentor profile saved!
                    </span>
                  )}
                  {mentorSaveStatus === 'error' && (
                    <span className="text-red-600 dark:text-red-400" role="alert" data-testid="mentor-profile-save-error">
                      {mentorSaveError}
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Current Avatar Preview */}
          <div className="flex items-center gap-6">
            <img src={selectedAvatar || defaultAvatar(profile?.gender)} alt="Your avatar" className="w-24 h-24 rounded-full object-cover border-4 border-primary" />
            <div>
              <h2 className="font-heading text-xl font-medium text-text-main">{displayName || 'Your Name'}</h2>
              <p className="text-text-muted">{profile?.email}</p>
              <span className={`inline-block mt-2 px-3 py-1 text-sm rounded-full ${profile?.gender === 'sister' ? 'bg-pink-100 text-pink-700' : 'bg-blue-100 text-blue-700'}`}>
                {profile?.gender === 'sister' ? 'Sister' : 'Brother'}
              </span>
            </div>
          </div>

          {/* Avatar Selection */}
          <div>
            <label className="block text-sm font-medium text-text-main mb-4">
              Choose Avatar {profile?.gender === 'sister' ? '(Sister Avatars)' : '(Brother Avatars)'}
            </label>
            {avatarsLoading ? (
              <div className="text-center py-4 text-text-muted">Loading avatars...</div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
                {avatarOptions.map((avatar) => (
                  <button
                    key={avatar.id}
                    onClick={() => setSelectedAvatar(avatar.url)}
                    className={`flex flex-col items-center gap-1 transition-all hover:scale-105 ${
                      selectedAvatar === avatar.url ? 'scale-105' : ''
                    }`}
                    data-testid={`avatar-option-${avatar.id}`}
                  >
                    <div className={`aspect-square rounded-full overflow-hidden border-4 transition-all ${
                      selectedAvatar === avatar.url
                        ? 'border-primary shadow-lg shadow-primary/30'
                        : 'border-transparent hover:border-secondary'
                    }`}>
                      <img src={avatar.url} alt={avatar.name} className="w-full h-full object-cover" />
                    </div>
                    <span className="text-xs text-text-muted truncate max-w-full">{avatar.name}</span>
                  </button>
                ))}
              </div>
            )}
            {/* Admin-only: upload a custom avatar */}
            {profile?.is_admin && (
              <div className="mt-4 pt-4 border-t border-white/20">
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-arch cursor-pointer transition-colors text-sm font-medium"
                  data-testid="admin-custom-avatar-upload">
                  <Upload className="w-4 h-4" />
                  {customUploading ? 'Uploading...' : 'Upload Custom Avatar (Admin Only)'}
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                    onChange={handleCustomAvatarUpload} className="hidden" disabled={customUploading} />
                </label>
              </div>
            )}
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-text-main mb-2">Display Name</label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
              <input type="text" value={displayName}
                onChange={(e) => setDisplayName(e.target.value.replace(/[^a-zA-Z.]/g, '').slice(0, maxNameLen))}
                maxLength={maxNameLen}
                className={`w-full pl-12 pr-10 py-3 bg-white/70 dark:bg-white dark:text-gray-800 rounded-arch border focus:ring-2 focus:ring-primary transition-all ${
                  nameStatus === 'taken' || nameStatus === 'invalid' ? 'border-red-400' :
                  nameStatus === 'available' ? 'border-green-400' : 'border-primary/20'
                }`}
                placeholder="Your.Name" data-testid="profile-name-input" />
              {/* Status indicator */}
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                {nameStatus === 'checking' && <span className="text-xs text-text-muted">...</span>}
                {nameStatus === 'available' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                {nameStatus === 'taken' && <span className="text-xs text-red-500 font-medium">Taken</span>}
                {nameStatus === 'invalid' && <span className="text-xs text-red-500 font-medium">!</span>}
              </div>
            </div>
            {nameError && <p className="text-xs text-red-500 mt-1">{nameError}</p>}
            <p className="text-[10px] text-text-muted mt-1">Letters and periods only. Case is preserved (uniqueness is case-insensitive). Max {maxNameLen} characters.</p>
          </div>

          {/* Bio */}
          <div>
            <label className="block text-sm font-medium text-text-main mb-2">Bio</label>
            <textarea value={bio} onChange={(e) => setBio(e.target.value)}
              className="w-full px-4 py-3 bg-white/70 dark:bg-white dark:text-gray-800 rounded-arch border border-primary/20 focus:ring-2 focus:ring-primary transition-all resize-none"
              rows={4} placeholder="Tell the community about yourself and your journey..." data-testid="profile-bio-input" />
          </div>

          {/* Community Visibility Toggles */}
          <div className="p-6 bg-white/30 dark:bg-white/5 rounded-arch-lg">
            <h3 className="font-heading text-lg font-medium text-text-main mb-2">Community Visibility</h3>
            <p className="text-sm text-text-muted mb-6">
              Choose which community members you'd like to see posts from. Both are enabled by default for a co-ed experience.
            </p>
            <div className="space-y-4">
              <label className="flex items-center justify-between cursor-pointer p-3 bg-white/50 dark:bg-white/10 rounded-arch">
                <div>
                  <span className="font-medium text-text-main">Show posts from Brothers</span>
                  <p className="text-sm text-text-muted">See posts and comments from male members</p>
                </div>
                <button type="button" onClick={() => setShowBrothers(!showBrothers)}
                  className={`relative w-14 h-7 rounded-full transition-colors flex-shrink-0 ${showBrothers ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
                  data-testid="toggle-show-brothers">
                  <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform shadow ${showBrothers ? 'translate-x-7' : 'translate-x-0'}`} />
                </button>
              </label>
              <label className="flex items-center justify-between cursor-pointer p-3 bg-white/50 dark:bg-white/10 rounded-arch">
                <div>
                  <span className="font-medium text-text-main">Show posts from Sisters</span>
                  <p className="text-sm text-text-muted">See posts and comments from female members</p>
                </div>
                <button type="button" onClick={() => setShowSisters(!showSisters)}
                  className={`relative w-14 h-7 rounded-full transition-colors flex-shrink-0 ${showSisters ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
                  data-testid="toggle-show-sisters">
                  <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform shadow ${showSisters ? 'translate-x-7' : 'translate-x-0'}`} />
                </button>
              </label>
            </div>
          </div>

          {/* Profile Visibility */}
          <div className="glass-card rounded-arch-xl p-6" data-testid="profile-visibility-section">
            <h3 className="font-heading text-lg font-medium text-text-main mb-2">Profile Visibility</h3>
            <p className="text-sm text-text-muted mb-4">Who can view your public profile page</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'everyone', label: 'Everyone', emoji: '🌍' },
                { value: 'sisters', label: 'Sisters only', emoji: '🧕🏼' },
                { value: 'brothers', label: 'Brothers only', emoji: '🧔🏻‍♂️' },
                { value: 'private', label: 'Private', emoji: '🔒' },
              ].map(opt => (
                <button key={opt.value} onClick={() => setProfileVisibility(opt.value)}
                  className={`p-3 max-sm:p-2 rounded-lg border-2 text-left transition-all ${
                    profileVisibility === opt.value
                      ? 'border-primary bg-primary/10'
                      : 'border-primary/10 hover:border-primary/30'
                  }`}
                  data-testid={`visibility-${opt.value}`}>
                  <span className="text-lg max-sm:text-sm mr-1.5">{opt.emoji}</span>
                  <span className="text-sm max-sm:text-xs font-medium text-text-main">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Blocked Users */}
          <div className="glass-card rounded-arch-xl p-6" data-testid="blocked-users-section">
            <h3 className="font-heading text-lg font-medium text-text-main mb-4">Blocked Users</h3>
            {blockedUsers.length === 0 ? (
              <p className="text-sm text-text-muted">No blocked users.</p>
            ) : (
              <div className="space-y-3">
                {blockedUsers.map(bu => (
                  <div key={bu.id} className="flex items-center justify-between p-3 bg-white/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <img src={bu.avatar_url || defaultAvatar(bu.gender)}
                        alt={bu.display_name} className="w-8 h-8 rounded-full" />
                      <span className="text-sm font-medium text-text-main">{bu.display_name}</span>
                    </div>
                    <button onClick={async () => {
                      try {
                        await api.unblockUser(bu.id, profile.id);
                        setBlockedUsers(prev => prev.filter(b => b.id !== bu.id));
                      } catch {}
                    }}
                      className="px-3 py-1 text-xs text-red-500 hover:bg-red-50 rounded-lg"
                      data-testid={`unblock-${bu.id}`}>Unblock</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Invite Link */}
          {profile?.display_name && (
            <div className="p-4 bg-white/30 dark:bg-white/5 rounded-arch-lg">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-medium text-text-main">Your Invite Link</h3>
              </div>
              <p className="text-xs text-text-muted mb-3">
                Share this link — earn the Community Organizer badge when 3 friends join.
                {inviteStats?.referral_count > 0 && (
                  <span className="ml-1 font-medium text-primary">
                    {inviteStats.referral_count} {inviteStats.referral_count === 1 ? 'friend' : 'friends'} joined so far.
                  </span>
                )}
              </p>
              <div className="flex items-center gap-2">
                <div className="flex-1 px-3 py-2 bg-primary/5 rounded-arch border border-primary/20 text-xs text-text-muted truncate select-all">
                  sirati.co/invite/{profile.display_name}
                </div>
                <button
                  onClick={handleCopyInviteLink}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-primary text-primary-foreground rounded-arch text-xs font-medium hover:bg-primary/90 transition-colors"
                  data-testid="copy-invite-link-btn"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          {/* Delete Account confirmation */}
          {showDeleteConfirm && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-arch-lg border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-700 dark:text-red-300 mb-3">
                Are you sure? Your account will be permanently deleted. Your posts will remain in the community anonymously.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteLoading}
                  className="flex-1 py-2 bg-red-500 hover:bg-red-600 text-white rounded-full text-sm font-medium transition-colors disabled:opacity-50"
                  data-testid="confirm-delete-account-btn"
                >
                  {deleteLoading ? 'Deleting...' : 'Yes, delete my account'}
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleteLoading}
                  className="px-4 py-2 text-sm text-text-muted hover:text-text-main transition-colors"
                  data-testid="cancel-delete-account-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Save / Cancel / Delete row */}
          <div className="flex items-center gap-3 max-sm:gap-1.5 pt-4">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={loading || showDeleteConfirm}
              className="flex items-center gap-1.5 px-4 py-3 max-sm:px-2 max-sm:py-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-arch text-sm max-sm:text-xs font-medium transition-colors disabled:opacity-40"
              data-testid="delete-account-btn"
            >
              <Trash2 className="w-4 h-4 max-sm:w-3.5 max-sm:h-3.5" />
              <span className="max-sm:hidden">Delete Account</span>
              <span className="hidden max-sm:inline">Delete</span>
            </button>
            <div className="flex-1" />
            <button onClick={onBack} className="px-6 py-3 max-sm:px-3 max-sm:py-2 text-text-muted hover:text-text-main hover:bg-white/50 rounded-arch font-medium max-sm:text-sm transition-colors">
              Cancel
            </button>
            <button onClick={handleSave} disabled={loading}
              className="px-8 py-3 max-sm:px-3 max-sm:py-2 bg-primary text-white rounded-arch font-medium max-sm:text-sm btn-hover flex items-center gap-2 max-sm:gap-1 disabled:opacity-50"
              data-testid="save-profile-btn">
              {loading ? 'Saving...' : 'Save Changes'}
              <Save className="w-5 h-5 max-sm:w-4 max-sm:h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ProfilePage;
